/**
 * 天候・気温と売上の相関分析コンポーネント
 */

import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiClient';
import {
  CloudRain, Sun, Cloud, Thermometer, Wind,
  TrendingUp, TrendingDown, Calendar, Download, Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

import PageContainer from '../layout/PageContainer';
interface WeatherData {
  date: string;
  weather: '晴れ' | '曇り' | '雨' | '雪';
  temp_morning: number;
  temp_afternoon: number;
  temp_evening: number;
  humidity: number;
  wind_speed: number;
  precipitation: number;
  sales: number;
  customer_count: number;
}

interface WeatherStats {
  weather_sales: {
    sunny: { avg_sales: number; count: number };
    cloudy: { avg_sales: number; count: number };
    rainy: { avg_sales: number; count: number };
    snowy: { avg_sales: number; count: number };
  };
  temp_correlation: number;
  best_weather: string;
  worst_weather: string;
}

const WeatherSalesAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [weatherData, setWeatherData] = useState<WeatherData[]>([]);
  const [stats, setStats] = useState<WeatherStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [showInputModal, setShowInputModal] = useState(false);
  const [inputData, setInputData] = useState({
    date: new Date().toISOString().split('T')[0],
    weather: '晴れ' as '晴れ' | '曇り' | '雨' | '雪',
    temp_morning: 20,
    temp_afternoon: 25,
    temp_evening: 22
  });

  useEffect(() => {
    fetchWeatherData();
  }, [period]);

  const fetchWeatherData = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/analytics/weather-sales?period=${period}`);
      const data = await response.json();
      setWeatherData(data.data || generateDemoData());
      setStats(data.stats || generateDemoStats());
    } catch (error) {
      // デモデータを使用
      setWeatherData(generateDemoData());
      setStats(generateDemoStats());
    }
  };

  const fetchRealWeatherData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/weather/current`);
      const data = await response.json();
      
      if (data.success) {
        // 取得したデータを入力フォームに反映
        const currentTemp = data.data.temperature || 20;
        setInputData(prev => ({
          ...prev,
          date: new Date().toISOString().split('T')[0],
          weather: data.data.weather as '晴れ' | '曇り' | '雨' | '雪',
          temp_morning: Math.round(currentTemp - 2),
          temp_afternoon: Math.round(currentTemp),
          temp_evening: Math.round(currentTemp - 1)
        }));
        
        // データを保存
        await handleManualInputSubmit({
          date: new Date().toISOString().split('T')[0],
          weather: data.data.weather,
          temp_morning: Math.round(currentTemp - 2),
          temp_afternoon: Math.round(currentTemp),
          temp_evening: Math.round(currentTemp - 1)
        });
        
        alert(`天気データを取得しました！\n場所: ${data.location}\n天気: ${data.data.weather}\n現在気温: ${currentTemp}℃\n湿度: ${data.data.humidity}%`);
        
        // データを再取得
        fetchWeatherData();
      } else {
        alert('気象データの取得に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching real weather:', error);
      alert('気象データの取得中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleManualInputSubmit = async (data: any) => {
    try {
      const submitData = data || inputData;
      const response = await fetch(`${getApiUrl()}/weather/manual-input`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });
      
      if (response.ok) {
        setShowInputModal(false);
        fetchWeatherData();
      }
    } catch (error) {
      console.error('Error saving weather data:', error);
    }
  };

  const generateDemoData = (): WeatherData[] => {
    const data: WeatherData[] = [];
    const weathers = ['晴れ', '曇り', '雨', '晴れ', '晴れ', '曇り'] as const;
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const weather = weathers[Math.floor(Math.random() * weathers.length)];
      const baseTemp = 15 + Math.random() * 20;
      
      data.push({
        date: date.toISOString().split('T')[0],
        weather: weather,
        temp_morning: Math.round(baseTemp - 3 + Math.random() * 2),
        temp_afternoon: Math.round(baseTemp + 5 + Math.random() * 3),
        temp_evening: Math.round(baseTemp + Math.random() * 2),
        humidity: Math.round(40 + Math.random() * 40),
        wind_speed: Math.round(Math.random() * 10),
        precipitation: weather === '雨' ? Math.round(5 + Math.random() * 20) : 0,
        sales: Math.round(
          (weather === '晴れ' ? 150000 : weather === '雨' ? 120000 : 135000) +
          Math.random() * 50000
        ),
        customer_count: Math.round(
          (weather === '晴れ' ? 90 : weather === '雨' ? 70 : 80) +
          Math.random() * 30
        )
      });
    }
    
    return data;
  };

  const generateDemoStats = (): WeatherStats => {
    return {
      weather_sales: {
        sunny: { avg_sales: 158000, count: 15 },
        cloudy: { avg_sales: 142000, count: 8 },
        rainy: { avg_sales: 125000, count: 6 },
        snowy: { avg_sales: 110000, count: 1 }
      },
      temp_correlation: 0.68,
      best_weather: '晴れ',
      worst_weather: '雨'
    };
  };

  const handleManualInput = async () => {
    try {
      await fetch(`${getApiUrl()}/weather/manual-input`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inputData)
      });
      setShowInputModal(false);
      fetchWeatherData();
    } catch (error) {
      console.error('手動入力エラー:', error);
    }
  };

  // 天候別売上データ
  const weatherSalesData = stats ? [
    { name: '晴れ', sales: stats.weather_sales.sunny.avg_sales, days: stats.weather_sales.sunny.count, color: '#FDB813' },
    { name: '曇り', sales: stats.weather_sales.cloudy.avg_sales, days: stats.weather_sales.cloudy.count, color: '#94A3B8' },
    { name: '雨', sales: stats.weather_sales.rainy.avg_sales, days: stats.weather_sales.rainy.count, color: '#60A5FA' },
    { name: '雪', sales: stats.weather_sales.snowy.avg_sales, days: stats.weather_sales.snowy.count, color: '#E0E7FF' }
  ] : [];

  // 気温と売上の相関データ
  const tempSalesData = weatherData.map(d => ({
    temp: d.temp_afternoon,
    sales: d.sales,
    weather: d.weather
  }));

  // 天候アイコンの取得
  const getWeatherIcon = (weather: string) => {
    switch (weather) {
      case '晴れ': return <Sun className="w-5 h-5 text-yellow-500" />;
      case '曇り': return <Cloud className="w-5 h-5 text-gray-500" />;
      case '雨': return <CloudRain className="w-5 h-5 text-blue-500" />;
      default: return <Cloud className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <CloudRain className="w-8 h-8 text-blue-600" />
            天候・気温分析
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/home')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Home className="w-5 h-5" />
              ホーム
            </button>
            <button
              onClick={() => setShowInputModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              天候データ入力
            </button>
            <button 
              onClick={fetchRealWeatherData}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
            >
              {loading ? '取得中...' : '実際の天気を取得'}
            </button>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as any)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="week">1週間</option>
              <option value="month">1ヶ月</option>
              <option value="quarter">3ヶ月</option>
              <option value="year">1年</option>
            </select>
          </div>
        </div>

        {/* 統計カード */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Sun className="w-6 h-6 text-yellow-600" />
              <span className="text-sm text-gray-600">晴れの日</span>
            </div>
            <div className="text-2xl font-bold">¥{stats?.weather_sales.sunny.avg_sales.toLocaleString()}</div>
            <div className="text-sm text-gray-600">{stats?.weather_sales.sunny.count}日間の平均</div>
          </div>

          <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <CloudRain className="w-6 h-6 text-blue-600" />
              <span className="text-sm text-gray-600">雨の日</span>
            </div>
            <div className="text-2xl font-bold">¥{stats?.weather_sales.rainy.avg_sales.toLocaleString()}</div>
            <div className="text-sm text-gray-600">{stats?.weather_sales.rainy.count}日間の平均</div>
          </div>

          <div className="bg-gradient-to-r from-orange-100 to-orange-200 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Thermometer className="w-6 h-6 text-orange-600" />
              <span className="text-sm text-gray-600">気温相関</span>
            </div>
            <div className="text-2xl font-bold">{(stats?.temp_correlation || 0) * 100}%</div>
            <div className="text-sm text-gray-600">売上との相関係数</div>
          </div>

          <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-6 h-6 text-green-600" />
              <span className="text-sm text-gray-600">最適天候</span>
            </div>
            <div className="text-2xl font-bold">{stats?.best_weather}</div>
            <div className="text-sm text-gray-600">最も売上が高い</div>
          </div>
        </div>
      </div>

      {/* グラフエリア */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 天候別平均売上 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">天候別平均売上</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={weatherSalesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value: any) => `¥${value.toLocaleString()}`} />
              <Bar dataKey="sales" fill="#3B82F6">
                {weatherSalesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 気温と売上の相関 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">気温と売上の相関</h3>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="temp" name="気温" unit="℃" />
              <YAxis dataKey="sales" name="売上" />
              <Tooltip 
                formatter={(value: any, name: string) => {
                  if (name === 'sales') return `¥${value.toLocaleString()}`;
                  return `${value}℃`;
                }}
              />
              <Scatter name="売上" data={tempSalesData} fill="#8884d8" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* 日別天候と売上推移 */}
        <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">日別天候と売上推移</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left">日付</th>
                  <th className="px-4 py-2 text-center">天候</th>
                  <th className="px-4 py-2 text-right">朝の気温</th>
                  <th className="px-4 py-2 text-right">昼の気温</th>
                  <th className="px-4 py-2 text-right">夜の気温</th>
                  <th className="px-4 py-2 text-right">売上</th>
                  <th className="px-4 py-2 text-right">客数</th>
                </tr>
              </thead>
              <tbody>
                {weatherData.slice(0, 10).map((day, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2">{day.date}</td>
                    <td className="px-4 py-2 text-center">{getWeatherIcon(day.weather)}</td>
                    <td className="px-4 py-2 text-right">{day.temp_morning}℃</td>
                    <td className="px-4 py-2 text-right">{day.temp_afternoon}℃</td>
                    <td className="px-4 py-2 text-right">{day.temp_evening}℃</td>
                    <td className="px-4 py-2 text-right">¥{day.sales.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{day.customer_count}名</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 手動入力モーダル */}
      {showInputModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">天候データ手動入力</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">日付</label>
                <input
                  type="date"
                  value={inputData.date}
                  onChange={(e) => setInputData({...inputData, date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">天候</label>
                <select
                  value={inputData.weather}
                  onChange={(e) => setInputData({...inputData, weather: e.target.value as '晴れ' | '曇り' | '雨' | '雪'})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="晴れ">晴れ</option>
                  <option value="曇り">曇り</option>
                  <option value="雨">雨</option>
                  <option value="雪">雪</option>
                </select>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">朝の気温</label>
                  <input
                    type="number"
                    value={inputData.temp_morning}
                    onChange={(e) => setInputData({...inputData, temp_morning: Number(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">昼の気温</label>
                  <input
                    type="number"
                    value={inputData.temp_afternoon}
                    onChange={(e) => setInputData({...inputData, temp_afternoon: Number(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">夜の気温</label>
                  <input
                    type="number"
                    value={inputData.temp_evening}
                    onChange={(e) => setInputData({...inputData, temp_evening: Number(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowInputModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                キャンセル
              </button>
              <button
                onClick={handleManualInput}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherSalesAnalysis;